<template>
  <div
    ref="root"
    class="account-menu"
  >
    <button
      type="button"
      class="account-menu__trigger"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      @click="isOpen = !isOpen"
    >
      <username-badge :first-last="initials" />
      <v-icon
        name="caret-down"
        height="16"
        width="16"
        class="account-menu__caret"
        :rotate="isOpen ? 0 : 180"
      />
    </button>

    <div
      v-if="isOpen"
      class="account-menu__panel"
      role="menu"
    >
      <div class="account-menu__header">
        <div
          v-if="merchantName"
          class="account-menu__header-name"
        >
          {{ merchantName }}
        </div>
        <div
          v-if="email"
          class="account-menu__header-email"
        >
          {{ email }}
        </div>
        <div
          v-if="venuesCount"
          class="account-menu__header-venues"
        >
          {{ t('accountMenu.venuesCount', { count: venuesCount }) }}
        </div>
      </div>

      <button
        type="button"
        class="account-menu__item"
        role="menuitem"
        @click="onLogoutClick"
      >
        <v-icon
          name="signout"
          height="20"
          width="20"
        />
        {{ t('main.signout') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useModal } from 'vue-final-modal';
import UsernameBadge from './UsernameBadge.vue';
import { useClickOutside } from '@/composables/useClickOutside';
import { getPointsName } from '@/services/points.service';
import LogoutConfirmModal from '@/components/UI/modals/LogoutConfirmModal.vue';

const store = useStore();
const { t } = useI18n();

const root = ref(null);
const isOpen = ref(false);

const merchant = computed(() => store.getters.merchant || {});
const merchantName = computed(() => merchant.value?.name || merchant.value?.merchantName || '');
const email = computed(() => merchant.value?.email || store.getters.user?.email || '');

// ponytail: count of merchant venues from the store's pointsNames cache;
// getPointsName() fills it with one GET /operator/points/names on first mount, then it's cached per merchant.
const venuesCount = computed(() => {
  const names = store.getters.pointsNames;
  return Array.isArray(names) ? names.length : 0;
});

const initials = computed(() => {
  const name = merchantName.value.trim();
  if (!name) return '';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
});

useClickOutside(root, () => {
  isOpen.value = false;
});

onMounted(() => {
  getPointsName();
});

const logoutModal = useModal({
  component: LogoutConfirmModal,
  attrs: {
    onClose() {
      logoutModal.close();
    },
    async onSubmit() {
      try {
        await store.dispatch('logout');
      } catch (error) {
        console.error('Error during logout:', error);
      }
      logoutModal.close();
    },
  },
});

function onLogoutClick() {
  isOpen.value = false;
  logoutModal.open();
}
</script>

<style lang="scss" scoped>
.account-menu {
  position: relative;

  &__trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px 4px 4px;
    border: 0;
    background: transparent;
    border-radius: 24px;
    cursor: pointer;
    font-family: inherit;

    &:hover {
      background: rgba(14, 14, 34, 0.04);
    }
  }

  &__caret {
    color: rgba(14, 14, 34, 0.4);
    transition: transform 0.2s ease;
  }

  &__panel {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    min-width: 240px;
    padding: 8px;
    background: var(--color-white);
    box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.08);
    border-radius: 16px;
    z-index: 20;
  }

  &__header {
    padding: 8px 12px 12px;
    border-bottom: 1px solid rgba(14, 14, 34, 0.08);
    margin-bottom: 8px;
  }

  &__header-name {
    font-weight: 500;
    font-size: 14px;
    line-height: 1.3;
    color: $primary-color-dark;
    word-break: break-word;
  }

  &__header-email {
    font-size: 12px;
    line-height: 1.3;
    color: rgba(14, 14, 34, 0.5);
    word-break: break-all;
  }

  &__header-venues {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.3;
    color: rgba(14, 14, 34, 0.5);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    background: transparent;
    border-radius: 8px;
    font-family: inherit;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.2;
    color: rgba(14, 14, 34, 0.8);
    text-decoration: none;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: rgba(14, 14, 34, 0.04);
    }
  }
}
</style>
